/*
  Holiday gift codes (BFBEANS / CYBERTOEBEANS, Nov 2026).

  A discount code can't add items to a cart, so this keeps the gift in the cart:
  while a promo's window is open, every set unit gets one gift unit. The gift line
  carries `_promo_gift: <code>`; the Paws by Kai Discounts function makes one unit
  of the gift product free per set unit once the code is entered at checkout.

  - 'options' rules (BFBEANS): the gift variant matches the set line's options by
    name (Size, Colour), like cart-bundle-offer. A spare the customer already added
    for that set counts as its gift, so no second one is added.
  - 'size' rules (CYBERTOEBEANS hat): the set's Size maps to a hat size. Counted per
    product, not per variant, so a size the customer picks in the cart sticks.
  - Gift lines of a promo that isn't running are removed, so nobody checks out
    with a "free" gift at full price after the window closes.

  Config comes from snippets/promo-gifts.liquid. `?promo_preview=CODE` forces a
  promo on for this browser tab (testing on a draft theme); `?promo_preview=off`
  clears it.
*/
(function () {
  const config = window.pbkPromoGifts;
  if (!config || !Array.isArray(config.promos)) return;

  const PREVIEW_KEY = 'pbkPromoPreview';
  const GIFT_PROP = '_promo_gift';

  function previewCode() {
    try {
      const param = new URLSearchParams(window.location.search).get('promo_preview');
      if (param === 'off') sessionStorage.removeItem(PREVIEW_KEY);
      else if (param) sessionStorage.setItem(PREVIEW_KEY, param.toUpperCase());
      return sessionStorage.getItem(PREVIEW_KEY);
    } catch (e) {
      return null;
    }
  }

  function isActive(promo, preview) {
    if (preview) return preview === promo.code;
    const now = Date.now();
    return now >= Date.parse(promo.startsAt) && now < Date.parse(promo.endsAt);
  }

  const productCache = {};
  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(`${window.Shopify.routes.root}products/${handle}.js`).then((r) =>
        r.ok ? r.json() : null
      );
    }
    return productCache[handle];
  }

  const lower = (s) => String(s || '').trim().toLowerCase();
  const optionValue = (item, name) => {
    const opt = (item.options_with_values || []).find((o) => lower(o.name) === lower(name));
    return opt ? opt.value : null;
  };

  function giftVariantFor(rule, gift, setLine) {
    if (rule.match === 'size') {
      const size = optionValue(setLine, rule.sizeOption || 'Size');
      const wanted = (rule.sizeMap || {})[String(size).toUpperCase()];
      return gift.variants.find((v) => v.options[0] === wanted) || null;
    }
    return (
      gift.variants.find((v) =>
        gift.options.every((opt, i) => {
          const wanted = optionValue(setLine, opt.name);
          return wanted === null || wanted === v.options[i];
        })
      ) || null
    );
  }

  // Returns {adds: [{id, quantity, properties}], changes: [{id: key, quantity}]}.
  async function plan(cart, preview) {
    const adds = [];
    const changes = [];
    const active = config.promos.filter((p) => isActive(p, preview));
    const activeCodes = active.map((p) => p.code);

    for (const item of cart.items) {
      const code = item.properties && item.properties[GIFT_PROP];
      if (code && !activeCodes.includes(code)) changes.push({id: item.key, quantity: 0});
    }

    for (const promo of active) {
      for (const rule of promo.rules) {
        const gift = await getProduct(rule.gift);
        if (!gift) continue;
        const setLines = cart.items.filter((i) => rule.triggers.includes(i.handle) && !i.properties?.[GIFT_PROP]);
        const giftLines = cart.items.filter((i) => i.handle === rule.gift && i.properties?.[GIFT_PROP] === promo.code);
        const paidLines = cart.items.filter((i) => i.handle === rule.gift && !i.properties?.[GIFT_PROP]);
        const properties = {[GIFT_PROP]: promo.code, [config.propertyLabel]: promo.note};

        if (rule.match === 'size') {
          const wanted = setLines.reduce((n, l) => n + l.quantity, 0);
          const have = paidLines.reduce((n, l) => n + l.quantity, 0) + giftLines.reduce((n, l) => n + l.quantity, 0);
          if (have < wanted) {
            // Size each missing hat from the set lines, in cart order.
            let missing = wanted - have;
            for (const line of setLines) {
              const variant = giftVariantFor(rule, gift, line);
              const qty = Math.min(missing, line.quantity);
              if (variant && qty > 0) adds.push({id: variant.id, quantity: qty, properties});
              missing -= qty;
              if (missing <= 0) break;
            }
          } else {
            let extra = Math.min(have - wanted, giftLines.reduce((n, l) => n + l.quantity, 0));
            for (const line of giftLines.slice().reverse()) {
              if (extra <= 0) break;
              const drop = Math.min(extra, line.quantity);
              changes.push({id: line.key, quantity: line.quantity - drop});
              extra -= drop;
            }
          }
          continue;
        }

        // 'options': one matching gift per set unit, per variant.
        const wantedByVariant = new Map();
        for (const line of setLines) {
          const variant = giftVariantFor(rule, gift, line);
          if (!variant) continue;
          wantedByVariant.set(variant.id, (wantedByVariant.get(variant.id) || 0) + line.quantity);
        }
        const sum = (lines, id) => lines.filter((l) => l.variant_id === id).reduce((n, l) => n + l.quantity, 0);
        for (const [id, wanted] of wantedByVariant) {
          const needed = Math.max(0, wanted - sum(paidLines, id));
          const have = sum(giftLines, id);
          if (have < needed) adds.push({id, quantity: needed - have, properties});
        }
        for (const id of new Set(giftLines.map((l) => l.variant_id))) {
          const needed = Math.max(0, (wantedByVariant.get(id) || 0) - sum(paidLines, id));
          let extra = sum(giftLines, id) - needed;
          for (const line of giftLines.filter((l) => l.variant_id === id).reverse()) {
            if (extra <= 0) break;
            const drop = Math.min(extra, line.quantity);
            changes.push({id: line.key, quantity: line.quantity - drop});
            extra -= drop;
          }
        }
      }
    }
    return {adds, changes};
  }

  let running = null;
  async function sync() {
    if (running) return running;
    running = (async () => {
      const preview = previewCode();
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((r) => r.json());
      const hasGiftLines = cart.items.some((i) => i.properties && i.properties[GIFT_PROP]);
      if (!hasGiftLines && !config.promos.some((p) => isActive(p, preview))) return false;

      const {adds, changes} = await plan(cart, preview);
      for (const change of changes) {
        await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(change),
        });
      }
      if (adds.length) {
        // Oversell is intended (gifts come out of sets); if a variant still refuses,
        // the cart just goes without that gift.
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({items: adds}),
        });
        if (!res.ok) console.warn('promo-gifts: gift not added', await res.text());
      }
      return adds.length > 0 || changes.length > 0;
    })().finally(() => {
      running = null;
    });
    return running;
  }

  async function refreshAfterSync() {
    let changed = false;
    try {
      changed = await sync();
    } catch (e) {
      console.warn('promo-gifts', e);
    }
    if (!changed) return;
    if (document.querySelector('cart-items')) {
      window.location.reload();
      return;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    fetch(`${window.Shopify.routes.root}?sections=cart-icon-bubble`)
      .then((r) => r.json())
      .then((sections) => {
        const html = new DOMParser()
          .parseFromString(sections['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section');
        if (html) bubble.innerHTML = html.innerHTML;
      });
  }

  // Swapping a gift hat's size: remove the line and add the new size with the same
  // gift properties. Captured so cart-items doesn't read it as a quantity change.
  document.addEventListener(
    'change',
    async (event) => {
      const select = event.target.closest && event.target.closest('[data-promo-gift-variant]');
      if (!select) return;
      event.stopPropagation();
      const key = select.dataset.lineKey;
      const quantity = Number(select.dataset.quantity) || 1;
      // Liquid's `json` filter writes line properties as [[key, value], ...].
      const raw = JSON.parse(select.dataset.properties || '{}');
      const properties = Array.isArray(raw) ? Object.fromEntries(raw) : raw;
      select.disabled = true;
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({id: key, quantity: 0}),
      });
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({items: [{id: Number(select.value), quantity, properties}]}),
      });
      window.location.reload();
    },
    true
  );

  window.PBKPromoGifts = {sync, refresh: refreshAfterSync};

  function start() {
    refreshAfterSync();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => refreshAfterSync());
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
